import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiUrl } from '../lib/api';
import {
  Check, AlertCircle, Download, ArrowRight, CreditCard, Calendar,
  TrendingUp, X, Crown, Sparkles, RotateCcw, Tag, ChevronRight,
} from 'lucide-react';
import PlatformNav from '../components/platform/PlatformNav';
import RegisterModal from '../components/marketing/RegisterModal';
import { useAuth } from '../hooks/useAuth';

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

function fmtAmount(cents, currency = 'usd') {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency.toUpperCase() }).format(cents / 100);
}

// ── Plan catalog (UI metadata only) ──────────────────────────────────────────

const CATALOG = {
  free: { tag: 'Free account' },
  pro: {
    tag: 'Pro · Investment Intelligence Daily',
    monthly: { price: '$14.95', sub: '/month' },
    annual:  { price: '$129',   sub: '/year',  save: 'Save $50.40 — 2 months free' },
    feats: [
      'Projection ranges — all 4 period finals',
      'Bull & bear case + Devil’s Advocate',
      'Personalized stock watchlist',
      'Daily audio briefing',
    ],
  },
  premium: {
    tag: 'Premium · Pro Trader',
    monthly: { price: '$24.95', sub: '/month' },
    annual:  { price: '$249',   sub: '/year',  save: 'Save ~17% — best value' },
    feats: [
      'Everything in Pro',
      'Real-time intraday alerts',
      'Priority customer support',
      'API access to projections',
      'Founding-rate price lock',
    ],
  },
};

// ── Portal overlay ────────────────────────────────────────────────────────────

function PortalOverlay({ url, onClose }) {
  const ref = useRef(null);
  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onClose(); }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="bp-portal-overlay" onClick={e => { if (e.target === ref.current) onClose(); }} ref={ref}>
      <div className="bp-portal-wrap">
        <button className="bp-portal-close" onClick={onClose}><X size={17} /></button>
        <iframe src={url} title="Manage Billing" className="bp-portal-iframe" allow="payment" />
      </div>
    </div>
  );
}

// ── Past-due alert ────────────────────────────────────────────────────────────

function PastDueAlert({ onOpenPortal }) {
  return (
    <div className="bill-alert">
      <div className="ic"><AlertCircle size={18} /></div>
      <div className="tx">
        <b>Your last payment failed</b>
        <span>Update your payment method to keep your subscription active.</span>
      </div>
      <button className="cta" onClick={onOpenPortal}>Update card <ArrowRight size={14} /></button>
    </div>
  );
}

// ── Current plan card ─────────────────────────────────────────────────────────

function CurrentPlan({ sub, onOpenPortal, onSwitchToAnnual }) {
  const plan = sub?.tier || 'free';
  const cycle = sub?.billing_interval || null;
  const isCanceling = sub?.cancel_at_period_end;
  const status = isCanceling ? 'canceled' : (sub?.status || 'free');
  const isFree = plan === 'free';
  const c = CATALOG[plan];
  const cycleData = !isFree && cycle ? c?.[cycle] : null;

  return (
    <div className="bill-plan">
      <div className="bill-plan-head">
        <div>
          <div className={`bill-plan-tag${isFree ? ' free' : ''}`}>
            {!isFree && <Sparkles size={11} />} {c?.tag || 'Free account'}
          </div>
          <div className="bill-plan-name">
            {isFree ? 'Daily Report' : `${plan.charAt(0).toUpperCase() + plan.slice(1)} · ${cycle === 'annual' ? 'Annual' : 'Monthly'}`}
          </div>
          <div className="bill-plan-price-row">
            <span className={`bill-plan-price${isFree ? ' free' : ''}`}>
              {isFree ? '$0' : (cycleData?.price ?? '—')}
            </span>
            <span className="bill-plan-price-sub">
              {isFree ? '/ always free' : (cycleData?.sub ?? '')}
            </span>
          </div>
        </div>
        <span className={`bill-status ${isFree ? 'free' : status}`}>
          {status === 'active' && <><span className="pulse" /> Active</>}
          {status === 'trialing' && <><span className="pulse" /> Trial</>}
          {status === 'past_due' && <><AlertCircle size={10} /> Past due</>}
          {status === 'canceled' && <>Canceling soon</>}
          {isFree && <>Free</>}
        </span>
      </div>

      {!isFree && (
        <div className="bill-plan-body">
          <div className="bill-meta-grid">
            <div className="bill-meta-row">
              <div className="bill-meta-l">{isCanceling ? 'Access until' : 'Next renewal'}</div>
              <div className={`bill-meta-v${isCanceling ? ' muted' : ''}`}>{fmtDate(sub?.current_period_end)}</div>
            </div>
            <div className="bill-meta-row">
              <div className="bill-meta-l">{isCanceling ? 'Then ends' : 'Next charge'}</div>
              <div className={`bill-meta-v${isCanceling ? ' muted' : ''}`}>
                {isCanceling ? '—' : `${cycleData?.price ?? ''} ${cycleData?.sub ?? ''}`}
              </div>
            </div>
          </div>

          {cycle === 'monthly' && status === 'active' && (
            <div className="bill-plan-actions">
              <div className="bill-cycle-nudge">
                <div className="ic"><TrendingUp size={16} /></div>
                <div className="tx">
                  <b>Switch to annual and save ~28%</b>
                  {plan === 'pro' ? 'Pay $129/yr instead of $14.95/mo — 2 months free.' : 'Pay $249/yr instead of $24.95/mo.'}
                </div>
              </div>
              <button className="bill-tile-cta gold" style={{ width: 'auto', padding: '11px 16px', flexShrink: 0 }} onClick={onSwitchToAnnual}>
                Switch to annual <ArrowRight size={14} />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Payment method card ───────────────────────────────────────────────────────

function PaymentMethod({ sub, onOpenPortal, onUpgrade }) {
  const isPaid = sub && sub.tier !== 'free' && sub.status !== 'canceled';
  return (
    <div className="bill-pm">
      <div className="bill-pm-tag">Payment method</div>
      {isPaid ? (
        <>
          <div className="bill-pm-card">
            <div className="bill-pm-brand"><CreditCard size={16} /></div>
            <div className="bill-pm-info">
              <div className="bill-pm-num">Managed by Stripe</div>
              <div className="bill-pm-exp">Update via billing portal</div>
            </div>
          </div>
          <div className="bill-pm-actions">
            <button className="bill-pm-btn" onClick={onOpenPortal}>Update card</button>
          </div>
        </>
      ) : (
        <>
          <div className="bill-pm-empty">
            <CreditCard size={22} /><br />
            <b style={{ fontFamily: 'var(--fh)', fontSize: 13, color: 'var(--ink2)' }}>No payment method</b><br />
            <span style={{ fontSize: 12 }}>Upgrade to add a card.</span>
          </div>
          <div className="bill-pm-actions">
            <button className="bill-pm-btn" style={{ background: 'var(--ink)', color: '#fff', borderColor: 'var(--ink)' }} onClick={onUpgrade}>
              Upgrade to Pro
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ── Upgrade tile ──────────────────────────────────────────────────────────────

function UpgradeTile({ tier, cycle, featured, onUpgrade }) {
  const c = CATALOG[tier];
  const data = c[cycle];
  const isPremium = tier === 'premium';
  return (
    <div className={`bill-tile${featured ? ' featured' : ''}`}>
      {featured && <div className="bill-tile-badge">Best value · 2 months free</div>}
      <div className="bill-tile-head">
        <div>
          <div className={`bill-tile-tag${isPremium ? ' premium' : ' pro'}`}>
            {isPremium ? <Crown size={11} /> : <Sparkles size={11} />}
            {c.tag}
          </div>
          <div className="bill-tile-name">{tier.charAt(0).toUpperCase() + tier.slice(1)} · {cycle === 'annual' ? 'Annual' : 'Monthly'}</div>
        </div>
      </div>
      <div className="bill-tile-price-row">
        <span className="bill-tile-price">{data.price}</span>
        <span className="bill-tile-price-sub">{data.sub}</span>
      </div>
      {data.save
        ? <div className="bill-tile-save">{data.save}</div>
        : <div style={{ height: 20 }} />}
      <ul className="bill-tile-feats">
        {c.feats.slice(0, 4).map((f, i) => (
          <li key={i} className={isPremium && i > 0 ? 'premium-only' : ''}>
            {isPremium && i > 0 ? <Check size={13} strokeWidth={3} style={{ color: 'var(--purple)' }} /> : <Check size={13} strokeWidth={3} />}
            {f}
          </li>
        ))}
      </ul>
      <button className={`bill-tile-cta${featured ? ' gold' : ' dark'}`} onClick={onUpgrade}>
        Upgrade to {tier.charAt(0).toUpperCase() + tier.slice(1)} <ArrowRight size={14} />
      </button>
    </div>
  );
}

function UpgradeSection({ plan, onUpgrade }) {
  const [pickCycle, setPickCycle] = useState('annual');

  if (plan === 'premium') {
    return (
      <div className="bill-topplan">
        <div className="ic"><Crown size={22} /></div>
        <div className="tx">
          <b>You&rsquo;re on Premium — our top plan</b>
          <span>You&rsquo;ve got everything we make. Thanks for backing the work.</span>
        </div>
      </div>
    );
  }

  const tiles = plan === 'free'
    ? [{ tier: 'pro', featured: pickCycle === 'annual' }, { tier: 'premium', featured: false }]
    : [{ tier: 'premium', featured: pickCycle === 'annual' }];

  return (
    <>
      <div className="bill-sec-head">
        <div>
          <div className="bill-sec-tag">Upgrade</div>
          <h2>{plan === 'free' ? 'Choose a plan' : 'Move up to Premium'}</h2>
        </div>
        <div className="bill-cycle-toggle">
          <button className={pickCycle === 'monthly' ? 'on' : ''} onClick={() => setPickCycle('monthly')}>Monthly</button>
          <button className={pickCycle === 'annual' ? 'on' : ''} onClick={() => setPickCycle('annual')}>
            Annual <span className="save">Save ~28%</span>
          </button>
        </div>
      </div>
      <div className={`bill-upgrades${tiles.length > 1 ? ' two-col' : ''}`}>
        {tiles.map((t, i) => (
          <UpgradeTile key={i} tier={t.tier} cycle={pickCycle} featured={t.featured} onUpgrade={() => onUpgrade(t.tier, pickCycle)} />
        ))}
      </div>
    </>
  );
}

// ── Billing history ───────────────────────────────────────────────────────────

function BillingHistory({ invoices, pdfLoading, onDownload }) {
  if (!invoices.length) {
    return (
      <div className="bill-table-wrap">
        <div className="bill-empty">
          <Calendar size={28} />
          <b>No invoices yet</b>
          <span>When you upgrade, invoices will appear here.</span>
        </div>
      </div>
    );
  }
  return (
    <div className="bill-table-wrap">
      <table className="bill-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Description</th>
            <th>Amount</th>
            <th>Status</th>
            <th className="right">Invoice</th>
          </tr>
        </thead>
        <tbody>
          {invoices.map(inv => (
            <tr key={inv.id}>
              <td className="date">{fmtDate(inv.created_at)}</td>
              <td className="desc">
                {inv.tier ? inv.tier.charAt(0).toUpperCase() + inv.tier.slice(1) : '—'}
                {inv.billing_interval ? ` · ${inv.billing_interval.charAt(0).toUpperCase() + inv.billing_interval.slice(1)}` : ''}
                <small>{fmtDate(inv.period_start)} – {fmtDate(inv.period_end)}</small>
              </td>
              <td className="amt">{fmtAmount(inv.amount_paid, inv.currency)}</td>
              <td>
                <span className={`bill-pill${inv.status === 'paid' ? ' paid' : inv.status === 'void' ? ' refunded' : ' failed'}`}>
                  {inv.status === 'paid' && <Check size={9} strokeWidth={3} />}
                  {inv.status !== 'paid' && inv.status !== 'void' && <X size={9} strokeWidth={3} />}
                  {inv.status}
                </span>
              </td>
              <td className="right">
                <button className="bill-dl-btn" onClick={() => onDownload(inv.id)} disabled={pdfLoading === inv.id}>
                  <Download size={11} /> {pdfLoading === inv.id ? '…' : 'PDF'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Danger zone ───────────────────────────────────────────────────────────────

function DangerZone({ sub, cancelLoading, confirmCancel, setConfirmCancel, onCancel, onReactivate }) {
  const plan = sub?.tier || 'free';
  if (plan === 'free') return null;
  const isCanceling = sub?.cancel_at_period_end;

  if (isCanceling) {
    return (
      <div className="bill-danger">
        <div className="tx">
          <b>Subscription canceling</b>
          <span>Your plan ends on {fmtDate(sub?.current_period_end)}. Reactivate anytime before then to keep your access.</span>
        </div>
        <button className="bill-danger-btn reactivate" onClick={onReactivate}>
          <RotateCcw size={12} /> Reactivate
        </button>
      </div>
    );
  }

  return (
    <>
      {confirmCancel && (
        <div className="bill-alert" style={{ marginTop: 12, borderColor: '#FCA5A5' }}>
          <div className="tx">
            <b>Cancel subscription?</b>
            <span>You'll keep access until {fmtDate(sub?.current_period_end)}. No refund for time already used.</span>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="cta" style={{ background: 'var(--red)' }} onClick={onCancel} disabled={cancelLoading}>
              {cancelLoading ? 'Canceling…' : 'Yes, cancel'}
            </button>
            <button className="cta" style={{ background: 'var(--ink)' }} onClick={() => setConfirmCancel(false)}>
              Keep plan
            </button>
          </div>
        </div>
      )}
      {!confirmCancel && (
        <div className="bill-danger">
          <div className="tx">
            <b>Cancel subscription</b>
            <span>You'll keep access until the end of your current billing period. No refund for time already used.</span>
          </div>
          <button className="bill-danger-btn" onClick={() => setConfirmCancel(true)}>Cancel subscription</button>
        </div>
      )}
    </>
  );
}

// ── BillingPage ───────────────────────────────────────────────────────────────

export default function BillingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [sub, setSub]             = useState(null);
  const [invoices, setInvoices]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [portalUrl, setPortalUrl]         = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [pdfLoading, setPdfLoading]       = useState(null);
  const [showUpgrade, setShowUpgrade]     = useState(false);
  const [upgradePlan, setUpgradePlan]     = useState('monthly');

  async function getToken() { return user.getIdToken(); }

  async function load() {
    setLoading(true); setError(null);
    try {
      const token = await getToken();
      const h = { Authorization: `Bearer ${token}` };
      const [subRes, invRes] = await Promise.all([
        fetch(apiUrl('/api/billing/subscription'), { headers: h }),
        fetch(apiUrl('/api/billing/invoices'),     { headers: h }),
      ]);
      if (subRes.ok) setSub(await subRes.json());
      if (invRes.ok) setInvoices(await invRes.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function openPortal() {
    setPortalLoading(true);
    try {
      const token = await getToken();
      const res = await fetch(apiUrl('/api/billing/portal'), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, Referer: window.location.href },
      });
      if (!res.ok) throw new Error(`${res.status}`);
      const { portal_url } = await res.json();
      setPortalUrl(portal_url);
    } catch (err) {
      alert(`Could not open billing portal: ${err.message}`);
    } finally {
      setPortalLoading(false);
    }
  }

  async function cancelSubscription() {
    setCancelLoading(true);
    try {
      const token = await getToken();
      const res = await fetch(apiUrl('/api/billing/cancel'), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`${res.status}`);
      setConfirmCancel(false);
      await load();
    } catch (err) {
      alert(`Cancel failed: ${err.message}`);
    } finally {
      setCancelLoading(false);
    }
  }

  async function downloadPdf(invoiceId) {
    setPdfLoading(invoiceId);
    try {
      const token = await getToken();
      const res = await fetch(apiUrl(`/api/billing/invoices/${invoiceId}/pdf`), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `invoice-${invoiceId}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert(`Download failed: ${err.message}`);
    } finally {
      setPdfLoading(null);
    }
  }

  function handleUpgrade(tier, cycle) {
    const plan = sub?.tier || 'free';
    if (plan !== 'free') {
      // Existing subscribers modify via portal
      openPortal();
    } else {
      setUpgradePlan(cycle);
      setShowUpgrade(true);
    }
  }

  const plan = sub?.tier || 'free';
  const isPastDue = sub?.status === 'past_due';

  return (
    <div className="platform-v2">
      <PlatformNav issueLabel="" />
      <div className="bill-scroll-area">

      {showUpgrade && (
        <RegisterModal
          initialStep="payment"
          initialPlan={upgradePlan}
          initialUser={user}
          onClose={() => { setShowUpgrade(false); load(); }}
        />
      )}
      {portalUrl && (
        <PortalOverlay url={portalUrl} onClose={() => { setPortalUrl(null); load(); }} />
      )}

      <div className="bill-page">
        <div className="bill-page-h">
          <div>
            <h1>Billing &amp; Subscription</h1>
            <p>Manage your plan, payment method, and download past invoices.</p>
          </div>
          <button className="bp-back" onClick={() => navigate('/platform')}>
            <ChevronRight size={14} style={{ transform: 'rotate(180deg)' }} /> Back to platform
          </button>
        </div>

        {error && <div className="bill-alert" style={{ marginBottom: 20 }}>
          <div className="ic"><AlertCircle size={18} /></div>
          <div className="tx"><b>Failed to load</b><span>{error}</span></div>
        </div>}

        {isPastDue && <PastDueAlert onOpenPortal={openPortal} />}

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}>
            <div className="spinner" style={{ width: 28, height: 28 }} />
          </div>
        ) : (
          <>
            {/* ── Plan + payment method row ── */}
            <div className="bill-sec">
              <div className="bill-row">
                <CurrentPlan
                  sub={sub}
                  onOpenPortal={openPortal}
                  onSwitchToAnnual={() => handleUpgrade(plan, 'annual')}
                />
                <PaymentMethod
                  sub={sub}
                  onOpenPortal={openPortal}
                  onUpgrade={() => handleUpgrade('pro', 'monthly')}
                />
              </div>
            </div>

            {/* ── Upgrade section ── */}
            {plan !== 'premium' && (
              <div className="bill-sec">
                <UpgradeSection plan={plan} onUpgrade={handleUpgrade} />
              </div>
            )}
            {plan === 'premium' && (
              <div className="bill-sec">
                <UpgradeSection plan={plan} onUpgrade={handleUpgrade} />
              </div>
            )}

            {/* ── Billing history ── */}
            <div className="bill-sec">
              <div className="bill-sec-head">
                <div>
                  <div className="bill-sec-tag">History</div>
                  <h2>Billing history</h2>
                </div>
              </div>
              <BillingHistory invoices={invoices} pdfLoading={pdfLoading} onDownload={downloadPdf} />
            </div>

            {/* ── Danger zone ── */}
            <DangerZone
              sub={sub}
              cancelLoading={cancelLoading}
              confirmCancel={confirmCancel}
              setConfirmCancel={setConfirmCancel}
              onCancel={cancelSubscription}
              onReactivate={openPortal}
            />
          </>
        )}
      </div>

      </div>{/* bill-scroll-area */}

      <style>{`
        .bill-scroll-area { flex: 1; overflow-y: auto; width: 100%; }

        .bill-page {
          max-width: 1080px; margin: 0 auto; padding: 32px 24px 80px;
          animation: fadeUp .25s ease;
        }
        @keyframes fadeUp { from { opacity: 0; transform: translateY(8px) } to { opacity: 1; transform: none } }
        @keyframes blink  { 0%,100% { opacity: 1 } 50% { opacity: .35 } }

        .bill-page-h {
          display: flex; align-items: flex-end; justify-content: space-between;
          margin-bottom: 28px; gap: 16px;
        }
        .bill-page-h h1 {
          font-family: var(--fh); font-size: 32px; font-weight: 800;
          letter-spacing: -.03em; line-height: 1.1; color: var(--ink);
        }
        .bill-page-h p { font-size: 14px; color: var(--muted); margin-top: 6px; }
        .bp-back {
          display: flex; align-items: center; gap: 4px;
          font-family: var(--fb); font-size: 13px; color: var(--muted);
          background: none; border: none; cursor: pointer; padding: 4px 0; flex-shrink: 0;
          transition: color .15s;
        }
        .bp-back:hover { color: var(--ink); }

        /* alert */
        .bill-alert {
          display: flex; align-items: center; gap: 12px;
          padding: 14px 18px; border: 1.5px solid #FCA5A5;
          background: var(--red-s); border-radius: var(--r2); margin-bottom: 22px;
        }
        .bill-alert .ic {
          width: 32px; height: 32px; border-radius: 8px;
          background: #fff; color: var(--red);
          display: flex; align-items: center; justify-content: center; flex-shrink: 0;
        }
        .bill-alert .tx { flex: 1; min-width: 0; }
        .bill-alert .tx b { font-family: var(--fh); font-size: 14px; color: var(--red-700, #b91c1c); display: block; letter-spacing: -.01em; }
        .bill-alert .tx span { font-size: 12.5px; color: var(--red-700, #b91c1c); opacity: .85; }
        .bill-alert .cta {
          font-family: var(--fh); font-size: 12px; font-weight: 700; letter-spacing: .02em;
          padding: 9px 14px; border-radius: var(--r); background: var(--red); color: #fff;
          display: inline-flex; align-items: center; gap: 6px; flex-shrink: 0;
          transition: all .15s; cursor: pointer; border: none;
        }
        .bill-alert .cta:hover { background: var(--red-700, #b91c1c); }

        /* sections */
        .bill-sec { margin-bottom: 32px; }
        .bill-sec-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; gap: 12px; }
        .bill-sec-tag { font-family: var(--fm); font-size: 10px; font-weight: 500; letter-spacing: .1em; text-transform: uppercase; color: var(--muted); }
        .bill-sec h2 { font-family: var(--fh); font-size: 18px; font-weight: 800; letter-spacing: -.02em; color: var(--ink); }

        /* top row */
        .bill-row { display: grid; grid-template-columns: 1.6fr 1fr; gap: 16px; }

        /* current plan card */
        .bill-plan {
          background: var(--bg, #fff); border: 1px solid var(--border);
          border-radius: var(--r3); box-shadow: var(--shadow); overflow: hidden;
        }
        .bill-plan-head {
          background: var(--ink); padding: 20px 22px; color: #fff;
          display: flex; align-items: flex-start; justify-content: space-between; gap: 16px;
        }
        .bill-plan-tag {
          font-family: var(--fm); font-size: 10px; font-weight: 500; letter-spacing: .1em;
          text-transform: uppercase; color: var(--amber2, #f59e0b); margin-bottom: 5px;
          display: flex; align-items: center; gap: 6px;
        }
        .bill-plan-tag.free { color: rgba(255,255,255,.5); }
        .bill-plan-name { font-family: var(--fh); font-size: 22px; font-weight: 800; color: #fff; letter-spacing: -.02em; line-height: 1.15; margin-bottom: 12px; }
        .bill-plan-price-row { display: flex; align-items: baseline; gap: 6px; }
        .bill-plan-price { font-family: var(--fh); font-size: 36px; font-weight: 800; color: var(--amber2, #f59e0b); letter-spacing: -.04em; line-height: 1; }
        .bill-plan-price.free { color: #fff; }
        .bill-plan-price-sub { font-size: 13px; color: rgba(255,255,255,.55); }

        .bill-status {
          display: inline-flex; align-items: center; gap: 5px;
          font-family: var(--fm); font-size: 10px; letter-spacing: .08em;
          text-transform: uppercase; font-weight: 600;
          padding: 4px 9px; border-radius: 5px; flex-shrink: 0;
        }
        .bill-status .pulse { width: 6px; height: 6px; border-radius: 50%; background: #22C55E; animation: blink 1.6s ease infinite; }
        .bill-status.active   { background: rgba(34,197,94,.18); color: #86EFAC; }
        .bill-status.trialing { background: rgba(34,197,94,.18); color: #86EFAC; }
        .bill-status.past_due { background: rgba(220,38,38,.2); color: #FCA5A5; }
        .bill-status.canceled { background: rgba(255,255,255,.1); color: rgba(255,255,255,.6); }
        .bill-status.free     { background: rgba(255,255,255,.08); color: rgba(255,255,255,.55); }

        .bill-plan-body { padding: 16px 22px; }
        .bill-meta-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; padding-bottom: 14px; border-bottom: 1px solid var(--border); }
        .bill-meta-row { display: flex; flex-direction: column; gap: 3px; }
        .bill-meta-l { font-family: var(--fm); font-size: 9.5px; letter-spacing: .1em; text-transform: uppercase; color: var(--muted); font-weight: 500; }
        .bill-meta-v { font-family: var(--fh); font-size: 14px; font-weight: 700; color: var(--ink); letter-spacing: -.01em; }
        .bill-meta-v.muted { color: var(--muted); font-weight: 600; }
        .bill-plan-actions { padding-top: 14px; display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
        .bill-cycle-nudge {
          flex: 1; min-width: 0; display: flex; align-items: center; gap: 10px;
          padding: 10px 12px; background: var(--amber-s, #fef3c7); border: 1px solid #FCD34D; border-radius: var(--r);
        }
        .bill-cycle-nudge .ic { color: var(--amber); display: flex; flex-shrink: 0; }
        .bill-cycle-nudge .tx { font-size: 12.5px; color: var(--amber); font-weight: 600; line-height: 1.4; }
        .bill-cycle-nudge .tx b { font-family: var(--fh); display: block; color: var(--amber); letter-spacing: -.01em; }

        /* payment method */
        .bill-pm { background: var(--bg, #fff); border: 1px solid var(--border); border-radius: var(--r3); box-shadow: var(--shadow); padding: 20px 22px; display: flex; flex-direction: column; }
        .bill-pm-tag { font-family: var(--fm); font-size: 10px; letter-spacing: .1em; text-transform: uppercase; color: var(--muted); font-weight: 500; margin-bottom: 14px; }
        .bill-pm-card { border: 1px solid var(--border); border-radius: var(--r2); padding: 14px; display: flex; align-items: center; gap: 12px; background: var(--surface); margin-bottom: 12px; }
        .bill-pm-brand { width: 42px; height: 30px; background: var(--bg, #fff); border: 1px solid var(--border); border-radius: 6px; display: flex; align-items: center; justify-content: center; color: var(--muted); flex-shrink: 0; }
        .bill-pm-info { flex: 1; min-width: 0; }
        .bill-pm-num { font-family: var(--fm); font-size: 13px; font-weight: 600; color: var(--ink); letter-spacing: .02em; }
        .bill-pm-exp { font-family: var(--fm); font-size: 11px; color: var(--muted); margin-top: 2px; }
        .bill-pm-empty { border: 1.5px dashed var(--border); border-radius: var(--r2); padding: 18px; text-align: center; color: var(--muted); font-size: 13px; margin-bottom: 12px; }
        .bill-pm-empty svg { color: var(--border); margin-bottom: 6px; }
        .bill-pm-actions { display: flex; gap: 8px; margin-top: auto; }
        .bill-pm-btn { flex: 1; padding: 9px 12px; border: 1.5px solid var(--border); border-radius: var(--r); background: var(--bg, #fff); color: var(--ink); font-family: var(--fh); font-size: 12px; font-weight: 700; letter-spacing: -.01em; display: flex; align-items: center; justify-content: center; gap: 6px; transition: all .15s; cursor: pointer; }
        .bill-pm-btn:hover { border-color: var(--ink); background: var(--surface); }

        /* upgrade tiles */
        .bill-upgrades { display: grid; gap: 14px; }
        .bill-upgrades.two-col { grid-template-columns: 1fr 1fr; }
        .bill-tile { position: relative; background: var(--bg, #fff); border: 1.5px solid var(--border); border-radius: var(--r2); padding: 18px 20px; display: flex; flex-direction: column; transition: all .15s; }
        .bill-tile:hover { border-color: var(--border2, #d1d1d6); box-shadow: var(--shadow); }
        .bill-tile.featured { border-color: var(--amber2, #f59e0b); box-shadow: 0 0 0 3px var(--amber-s, #fef3c7); }
        .bill-tile-badge { position: absolute; top: -9px; left: 18px; font-family: var(--fh); font-size: 9.5px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; background: var(--amber2, #f59e0b); color: var(--ink); padding: 3px 8px; border-radius: 5px; }
        .bill-tile-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 10px; margin-bottom: 6px; }
        .bill-tile-tag { font-family: var(--fm); font-size: 10px; letter-spacing: .1em; text-transform: uppercase; color: var(--muted); font-weight: 500; display: flex; align-items: center; gap: 5px; }
        .bill-tile-tag.premium { color: var(--purple, #7c3aed); }
        .bill-tile-tag.pro { color: var(--amber); }
        .bill-tile-name { font-family: var(--fh); font-size: 17px; font-weight: 800; letter-spacing: -.02em; color: var(--ink); margin-bottom: 10px; }
        .bill-tile-price-row { display: flex; align-items: baseline; gap: 5px; margin-bottom: 4px; }
        .bill-tile-price { font-family: var(--fh); font-size: 26px; font-weight: 800; color: var(--ink); letter-spacing: -.03em; line-height: 1; }
        .bill-tile-price-sub { font-size: 12px; color: var(--muted); }
        .bill-tile-save { font-family: var(--fm); font-size: 10px; letter-spacing: .06em; text-transform: uppercase; color: var(--green, #16a34a); font-weight: 600; margin-bottom: 12px; }
        .bill-tile-feats { list-style: none; padding: 0; margin: 8px 0 14px; display: flex; flex-direction: column; gap: 6px; }
        .bill-tile-feats li { display: flex; align-items: flex-start; gap: 8px; font-size: 12.5px; color: var(--ink2, var(--muted)); line-height: 1.45; }
        .bill-tile-feats li svg { color: var(--green); margin-top: 1px; }
        .bill-tile-cta { margin-top: auto; width: 100%; padding: 11px 14px; border-radius: var(--r); font-family: var(--fh); font-size: 13px; font-weight: 800; letter-spacing: -.01em; display: flex; align-items: center; justify-content: center; gap: 7px; transition: all .15s; cursor: pointer; border: none; }
        .bill-tile-cta.gold { background: var(--amber2, #f59e0b); color: var(--ink); }
        .bill-tile-cta.gold:hover { background: var(--amber); color: #fff; transform: translateY(-1px); }
        .bill-tile-cta.dark { background: var(--ink); color: #fff; }
        .bill-tile-cta.dark:hover { background: var(--amber); }

        /* cycle toggle */
        .bill-cycle-toggle { display: inline-flex; background: var(--surface); border: 1px solid var(--border); border-radius: 999px; padding: 3px; }
        .bill-cycle-toggle button { font-family: var(--fh); font-size: 12px; font-weight: 700; padding: 6px 14px; border-radius: 999px; color: var(--muted); letter-spacing: -.01em; cursor: pointer; border: none; background: none; }
        .bill-cycle-toggle button.on { background: var(--ink); color: #fff; }
        .bill-cycle-toggle .save { font-family: var(--fm); font-size: 9px; font-weight: 600; letter-spacing: .06em; color: var(--green); margin-left: 5px; }

        /* top plan */
        .bill-topplan { background: linear-gradient(135deg, #ede9fe 0%, #f5f3ff 100%); border: 1.5px solid #ddd6fe; border-radius: var(--r2); padding: 20px; display: flex; align-items: center; gap: 16px; }
        .bill-topplan .ic { width: 48px; height: 48px; border-radius: 12px; background: var(--purple, #7c3aed); color: #fff; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .bill-topplan .tx b { font-family: var(--fh); font-size: 16px; font-weight: 800; color: var(--purple, #7c3aed); display: block; letter-spacing: -.02em; }
        .bill-topplan .tx span { font-size: 13px; color: var(--purple, #7c3aed); opacity: .8; }

        /* history table */
        .bill-table-wrap { background: var(--bg, #fff); border: 1px solid var(--border); border-radius: var(--r2); overflow: hidden; }
        .bill-table { width: 100%; border-collapse: collapse; }
        .bill-table thead th { font-family: var(--fm); font-size: 10px; letter-spacing: .1em; text-transform: uppercase; color: var(--muted); font-weight: 500; padding: 11px 18px; text-align: left; border-bottom: 1px solid var(--border); background: var(--surface); }
        .bill-table thead th.right { text-align: right; }
        .bill-table tbody td { padding: 12px 18px; font-size: 13px; color: var(--ink2, var(--ink)); border-bottom: 1px solid var(--surface2, #f1f1f3); }
        .bill-table tbody tr:last-child td { border-bottom: none; }
        .bill-table tbody tr:hover { background: var(--surface); }
        .bill-table tbody td.right { text-align: right; }
        .bill-table td.date { font-family: var(--fm); font-size: 12px; }
        .bill-table td.desc { font-family: var(--fh); font-size: 13px; font-weight: 600; color: var(--ink); letter-spacing: -.01em; }
        .bill-table td.desc small { display: block; font-family: var(--fb); font-size: 11px; font-weight: 500; color: var(--muted); margin-top: 2px; letter-spacing: 0; }
        .bill-table td.amt { font-family: var(--fm); font-size: 13px; font-weight: 600; color: var(--ink); }
        .bill-pill { display: inline-flex; align-items: center; gap: 4px; font-family: var(--fm); font-size: 10px; letter-spacing: .06em; text-transform: uppercase; font-weight: 600; padding: 3px 8px; border-radius: 4px; }
        .bill-pill.paid { background: var(--green-s, #dcfce7); color: var(--green, #16a34a); }
        .bill-pill.failed { background: var(--red-s, #fee2e2); color: var(--red, #dc2626); }
        .bill-pill.refunded { background: var(--surface); color: var(--muted); }
        .bill-dl-btn { display: inline-flex; align-items: center; gap: 5px; padding: 5px 10px; border: 1px solid var(--border); border-radius: 6px; background: var(--bg, #fff); color: var(--ink2, var(--ink)); font-family: var(--fh); font-size: 11px; font-weight: 700; letter-spacing: -.01em; transition: all .15s; cursor: pointer; }
        .bill-dl-btn:hover { border-color: var(--ink); color: var(--ink); }
        .bill-dl-btn:disabled { opacity: .4; cursor: default; }
        .bill-empty { padding: 36px 20px; text-align: center; color: var(--muted); }
        .bill-empty svg { color: var(--border); margin-bottom: 8px; display: block; margin-left: auto; margin-right: auto; }
        .bill-empty b { font-family: var(--fh); font-size: 14px; color: var(--ink2, var(--ink)); display: block; margin-bottom: 3px; letter-spacing: -.01em; }
        .bill-empty span { font-size: 12.5px; }

        /* danger zone */
        .bill-danger { margin-top: 12px; background: var(--bg, #fff); border: 1px solid var(--border); border-radius: var(--r2); padding: 18px 22px; display: flex; align-items: center; gap: 16px; }
        .bill-danger .tx { flex: 1; }
        .bill-danger .tx b { font-family: var(--fh); font-size: 14px; font-weight: 700; letter-spacing: -.01em; color: var(--ink); display: block; margin-bottom: 2px; }
        .bill-danger .tx span { font-size: 12.5px; color: var(--muted); }
        .bill-danger-btn { font-family: var(--fh); font-size: 12px; font-weight: 700; letter-spacing: -.01em; padding: 9px 14px; border-radius: var(--r); border: 1.5px solid var(--border); background: var(--bg, #fff); color: var(--red); transition: all .15s; display: inline-flex; align-items: center; gap: 6px; cursor: pointer; }
        .bill-danger-btn:hover { border-color: #FCA5A5; background: var(--red-s, #fee2e2); }
        .bill-danger-btn.reactivate { color: var(--green, #16a34a); }
        .bill-danger-btn.reactivate:hover { border-color: #86EFAC; background: var(--green-s, #dcfce7); }

        /* portal overlay */
        .bp-portal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,.6); z-index: 9999; display: flex; align-items: stretch; justify-content: center; backdrop-filter: blur(2px); }
        .bp-portal-wrap { position: relative; width: 100%; max-width: 920px; margin: 24px; background: var(--bg, #fff); border-radius: var(--r2); overflow: hidden; display: flex; flex-direction: column; box-shadow: var(--shadow3); }
        .bp-portal-close { position: absolute; top: 12px; right: 12px; z-index: 1; background: rgba(0,0,0,.08); border: none; border-radius: 50%; width: 34px; height: 34px; display: flex; align-items: center; justify-content: center; cursor: pointer; color: var(--ink); transition: background .15s; }
        .bp-portal-close:hover { background: rgba(0,0,0,.16); }
        .bp-portal-iframe { flex: 1; width: 100%; border: none; min-height: 600px; }

        @media (max-width: 780px) {
          .bill-row { grid-template-columns: 1fr; }
          .bill-upgrades.two-col { grid-template-columns: 1fr; }
          .bill-table thead { display: none; }
          .bill-table td { display: block; border: none; padding: 6px 18px; }
          .bill-table td.date { padding-top: 14px; }
          .bill-table tr { border-bottom: 1px solid var(--border); }
        }
      `}</style>
    </div>
  );
}
